import type { StatValue } from "@/types/dashboard";
import { cn } from "@/lib/utils";
import { formatStat } from "./format-stat";

/** The four-up summary behind a table card: hairline cells, no boxes. */
export function StatGrid({
  stats,
  stretch = false,
}: {
  stats: StatValue[];
  /** Fill the parent's height, sharing it evenly between rows. */
  stretch?: boolean;
}) {
  // A single cell that would sit alone in its row (before a wide cell, or at
  // the end) stretches across it, so every row closes on the right edge.
  let column = 0;
  const spans = stats.map((stat, i) => {
    const next = stats[i + 1];
    const alone = !stat.wide && column === 0 && (!next || next.wide);
    const wide = stat.wide || alone;
    column = wide ? 0 : (column + 1) % 2;
    return wide;
  });

  return (
    <div className={cn("-ml-px -mt-px grid grid-cols-2", stretch && "min-h-0 flex-1 auto-rows-fr")}>
      {stats.map((stat, i) => (
        <div
          key={stat.label}
          className={cn(
            "flex min-w-0 flex-col gap-1 border-l border-t border-border px-4 py-3",
            spans[i] && "col-span-2"
          )}
        >
          <div className="text-xs font-medium text-muted-foreground">{stat.label}</div>
          <div
            className={cn(
              "font-semibold tracking-tight text-foreground",
              // Numbers stay on one line; names and titles may wrap to two.
              stat.kind === "text"
                ? "line-clamp-2 text-base leading-snug"
                : "truncate text-xl tabular-nums"
            )}
            title={stat.kind === "text" ? String(stat.value) : undefined}
          >
            {formatStat(stat)}
          </div>
          {stat.note && (
            <div className="truncate text-[11px] text-muted-foreground">{stat.note}</div>
          )}
        </div>
      ))}
    </div>
  );
}
